<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { Menu, PanelLeft, Sun, Moon, LogOut, KeyRound, ChevronDown } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import api from '@/api/axios'
import { useAuthStore } from '@/stores/auth'
import { useTheme } from '@/composables/useTheme'
import NotificationDropdown from '@/components/Notifications/NotificationDropdown.vue'
import { operatorStatuses } from './navItems'

const props = defineProps<{
  collapsed: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle-sidebar'): void
  (e: 'toggle-mobile'): void
}>()

const authStore = useAuthStore()
const { theme, toggleTheme } = useTheme()

const operatorStatus = ref<string>('available')
const loadingStatus = ref(false)

const isOperator = computed(() => authStore.isOperator)
const userFullName = computed(() => authStore.userFullName || 'Usuario')
const userRoleLabel = computed(() => {
  const roleId = authStore.user?.role_id ?? 0
  const labels: Record<number, string> = {
    1: 'Administrador',
    2: 'Supervisor',
    4: 'Operador',
    3: 'Generador',
  }
  return labels[roleId] ?? 'Usuario'
})

const currentStatus = computed(
  () => operatorStatuses.find((s) => s.value === operatorStatus.value) ?? operatorStatuses[0]
)

const initials = computed(() => {
  const name = authStore.user?.full_name || authStore.user?.email || 'U'
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
})

const syncStatus = async () => {
  if (!isOperator.value) return
  try {
    const { data } = await api.get('/api/v1/operators/me/status')
    operatorStatus.value = data.status || 'offline'
    // Revivir presencia al cargar: un operador con la app abierta no debe
    // quedar "offline" (p.ej. reabrió la app con sesión válida tras un cierre
    // de sesión, sin volver a hacer login). Solo se revive "offline"; los
    // estados manuales (break/backoffice/on_call) se respetan.
    if (operatorStatus.value === 'offline') {
      await handleStatusChange('available')
    }
  } catch {
    operatorStatus.value = 'offline'
  }
}

const handleStatusChange = async (value: string) => {
  if (loadingStatus.value) return
  const prev = operatorStatus.value
  operatorStatus.value = value
  loadingStatus.value = true
  try {
    const payload: Record<string, string> = { status: value }
    if (value === 'break') payload.break_reason = 'rest'
    const { data } = await api.put('/api/v1/operators/me/status', payload)
    operatorStatus.value = data.status || value
  } catch {
    operatorStatus.value = prev
  } finally {
    loadingStatus.value = false
  }
}

const handleLogout = () => {
  authStore.logout()
}

onMounted(() => {
  syncStatus()
})
</script>

<template>
  <header
    class="flex h-16 shrink-0 items-center justify-between border-b border-border bg-background px-4"
  >
    <!-- Left -->
    <div class="flex items-center gap-2">
      <!-- Mobile -->
      <Button variant="ghost" size="icon" class="md:hidden" @click="emit('toggle-mobile')">
        <Menu class="size-5" />
      </Button>
      <!-- Desktop -->
      <Button variant="ghost" size="icon" class="hidden md:inline-flex" @click="emit('toggle-sidebar')">
        <PanelLeft class="size-5" />
      </Button>
    </div>

    <!-- Right -->
    <div class="flex items-center gap-1 sm:gap-2">
      <!-- Theme toggle -->
      <Button variant="ghost" size="icon" class="hidden sm:inline-flex" @click="toggleTheme" aria-label="Cambiar tema">
        <Sun v-if="theme === 'dark'" class="size-5" />
        <Moon v-else class="size-5" />
      </Button>

      <!-- Operator status -->
      <DropdownMenu v-if="isOperator">
        <DropdownMenuTrigger as-child>
          <Button variant="outline" size="sm" class="gap-1.5 px-2 sm:gap-2 sm:px-3">
            <span :class="cn('size-2 rounded-full', currentStatus.color)" />
            <span class="hidden sm:inline">{{ currentStatus.label }}</span>
            <ChevronDown class="size-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-48">
          <DropdownMenuLabel>Estado del Operador</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            v-for="s in operatorStatuses"
            :key="s.value"
            class="gap-2"
            @select="handleStatusChange(s.value)"
          >
            <span :class="cn('size-2 rounded-full', s.color)" />
            {{ s.label }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <!-- Notifications -->
      <NotificationDropdown />

      <!-- User menu -->
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <button
            class="flex items-center gap-2 rounded-md p-1 sm:pr-2 transition-colors hover:bg-accent"
          >
            <Avatar class="size-8">
              <AvatarFallback class="bg-primary/10 text-primary text-xs font-medium">
                {{ initials }}
              </AvatarFallback>
            </Avatar>
            <span class="hidden text-sm font-medium lg:inline">{{ userFullName }}</span>
            <ChevronDown class="size-4 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-56">
          <DropdownMenuLabel>
            <div class="flex flex-col">
              <span class="font-medium">{{ userFullName }}</span>
              <span class="text-xs text-muted-foreground">{{ userRoleLabel }}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem class="gap-2" disabled>
            <KeyRound class="size-4" />
            Cambiar Contraseña
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem class="gap-2 text-destructive focus:text-destructive" @select="handleLogout">
            <LogOut class="size-4" />
            Cerrar Sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>
</template>